import { SaveToLibrary } from "./SaveToLibrary";
import { workspaceBottomInset } from "./workspace-layout";
import { useEffect, useRef, useState } from "react";
import {
  ArrowDownToLine,
  ArrowUpToLine,
  Box,
  Copy,
  Crop,
  Clapperboard,
  Download,
  Lock,
  MoreVertical,
  Pencil,
  SlidersHorizontal,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { Box as TLBox } from "./canvas";
import { useValue } from "@tldraw/state-react";
import type { TLImageShape, TLShape, TLShapeId } from "@tldraw/tlschema";
import type { BoardDocument, BoardTable } from "../shared/schema";
import type { WorkspaceModel } from "./useWorkspace";
import { DocumentFormatting, TableFormatting } from "./ContentFormatting";
import { displayObjectName } from "./object-name";
import { isCreativeImage, type CreativeMode } from "./creative-tools";
import {
  IMAGE_GENERATION_ENABLED,
  VIDEO_GENERATION_ENABLED,
} from "../shared/features";
import { modelAssetInfo, type ModelAssetInfo } from "./model-assets";
import { OpacityControl } from "./TraceControls";
import { nativeDocument } from "../shared/document-session";
import { MakeThreeDButton } from "./MakeThreeD";

type ContentShape = BoardDocument | BoardTable | TLImageShape;
type ContextMenu = {
  shapeId: string;
  x: number;
  y: number;
  nonce: number;
};

function contentShape(
  model: WorkspaceModel,
  shape?: TLShape | null,
): ContentShape | null {
  if (!shape) return null;
  if (nativeDocument(shape)) return null;
  if (shape.type === "document" || shape.type === "table") return shape;
  if (shape.type !== "image") return null;
  const asset =
    shape.props.assetId && model.editor.getAsset(shape.props.assetId);
  return asset?.meta.pdfPages && !shape.meta.traceSnapshot ? null : shape;
}

function contentName(model: WorkspaceModel, shape: ContentShape) {
  if (shape.type === "document" || shape.type === "table")
    return shape.props.name;
  const asset =
    shape.props.assetId && model.editor.getAsset(shape.props.assetId);
  return displayObjectName(
    shape.meta.name ||
      asset?.meta.documentName ||
      (asset?.type === "image" && asset.props.name) ||
      shape.props.altText,
  );
}

function originalSource(model: WorkspaceModel, shape: ContentShape) {
  if (shape.type === "document" || shape.type === "table")
    return shape.props.source;
  const asset =
    shape.props.assetId && model.editor.getAsset(shape.props.assetId);
  return asset?.meta.documentSource || asset?.props.src;
}

/** Compact controls follow the selected imported object, matching PDF behavior. */
export function ContentToolbar({
  model,
  onProperties,
  propertiesOpen = false,
  onCreative,
  onModel,
}: {
  model: WorkspaceModel;
  onProperties: (id: string) => void;
  propertiesOpen?: boolean;
  onCreative: (shape: TLImageShape, mode: CreativeMode) => void;
  onModel: (model: ModelAssetInfo) => void;
}) {
  const { editor } = model;
  const [panel, setPanel] = useState(false);
  const [opacityOpen, setOpacityOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState<ContextMenu | null>(null);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = editor.getContainer();
    const selectContent = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('button,input,select,textarea,a,[role="dialog"]'))
        return;
      const shape = contentShape(
        model,
        editor.getShapeAtPoint(
          editor.screenToPage({ x: event.clientX, y: event.clientY }),
          { hitInside: true },
        ),
      );
      if (!shape) return;
      event.preventDefault();
      event.stopPropagation();
      editor.select(shape.id);
      setContextMenu({
        shapeId: shape.id,
        x: event.clientX,
        y: event.clientY,
        nonce: Date.now(),
      });
    };
    container.addEventListener("contextmenu", selectContent, true);
    return () =>
      container.removeEventListener("contextmenu", selectContent, true);
  }, [editor, model]);

  const content = useValue(
    "selected content toolbar",
    () => {
      if (!editor.isIn("select")) return null;
      const shape = contentShape(model, editor.getOnlySelectedShape());
      if (!shape) return null;
      const bounds = editor.getShapePageBounds(shape);
      if (!bounds || !bounds.collides(editor.getViewportPageBounds()))
        return null;
      const viewport = editor.getViewportScreenBounds();
      const top = editor.pageToScreen({ x: bounds.center.x, y: bounds.minY });
      const bottom = editor.pageToScreen({
        x: bounds.center.x,
        y: bounds.maxY,
      });
      const editing = editor.getEditingShapeId() === shape.id;
      const availableWidth =
        viewport.w - (propertiesOpen && viewport.w >= 900 ? 272 : 0);
      const width = Math.min(
        editing ? 650 : shape.type === "image" ? 334 : 300,
        availableWidth - 32,
      );
      const above = top.y - viewport.y - 112;
      const below = bottom.y - viewport.y + 64;
      const y =
        above >= 136
          ? above
          : Math.min(viewport.h - workspaceBottomInset(viewport.w) - 70, below);
      return {
        shape,
        editing,
        width,
        x: Math.max(
          16,
          Math.min(availableWidth - width - 16, top.x - viewport.x - width / 2),
        ),
        y: Math.max(82, y),
        optionsAbove: y > viewport.h / 2,
      };
    },
    [editor, model, propertiesOpen],
  );
  useEffect(() => setOpacityOpen(false), [content?.shape.id]);

  useEffect(() => {
    if (
      model.canEdit &&
      !content?.shape.isLocked &&
      contextMenu?.shapeId === content?.shape.id
    )
      setPanel(true);
    else {
      setPanel(false);
      if (contextMenu) setContextMenu(null);
    }
  }, [
    content?.shape.id,
    content?.shape.isLocked,
    contextMenu?.nonce,
    model.canEdit,
  ]);

  useEffect(() => {
    if (!panel) return;
    const outside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setPanel(false);
    };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [panel]);

  if (!content || model.modal || model.cropping) return null;

  const { shape } = content;
  const asset =
    shape.type === "image" && shape.props.assetId
      ? editor.getAsset(shape.props.assetId)
      : undefined;
  const viewableModel = modelAssetInfo(asset);
  const editableShape = model.canEdit && !shape.isLocked;
  const nativeImage =
    shape.type === "image" &&
    !viewableModel &&
    !shape.meta.traceSnapshot &&
    !(asset?.meta.documentSource && !asset.meta.imageFile);
  const sketchSources =
    shape.type === "image" && Array.isArray(shape.meta.aiSources)
      ? shape.meta.aiSources
          .filter((id): id is string => typeof id === "string")
          .map((id) => editor.getShape(id as TLShapeId))
          .filter((source): source is TLShape => !!source)
      : [];
  if (!editableShape && !viewableModel) return null;
  const name = contentName(model, shape);
  const creative =
    !viewableModel &&
    shape.type === "image" &&
    isCreativeImage(shape, asset, model.room.id);
  const openCreative = (mode: CreativeMode) => {
    if (
      shape.type !== "image" ||
      !creative ||
      (mode === "video" && !VIDEO_GENERATION_ENABLED)
    )
      return;
    setPanel(false);
    onCreative(shape, mode);
  };
  const source = originalSource(model, shape);
  const downloadable =
    typeof source === "string" &&
    /^\/api\/rooms\/[\w-]+\/files\/[\w-]+$/.test(source);
  const edit = () => {
    setPanel(false);
    if (shape.type === "image") model.startCrop(shape);
    else {
      editor.setCurrentTool("select");
      editor.setEditingShape(shape.id);
    }
  };
  const contextPoint =
    contextMenu?.shapeId === shape.id
      ? {
          left: Math.max(8, Math.min(window.innerWidth - 248, contextMenu.x)),
          top: Math.max(8, Math.min(window.innerHeight - 330, contextMenu.y)),
        }
      : undefined;

  return (
    <div
      ref={root}
      className="pdf-object-toolbar content-object-toolbar floating-surface"
      data-options-above={content.optionsAbove}
      style={{ left: content.x, top: content.y, width: content.width }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          if (panel) setPanel(false);
          else if (opacityOpen) setOpacityOpen(false);
          else {
            editor.selectNone();
            editor.focus();
          }
        } else event.stopPropagation();
      }}
    >
      <section className="pdf-controls" aria-label={`${name} tools`}>
        {content.editing && shape.type !== "image" ? (
          <div
            className="content-edit-row"
            role="toolbar"
            aria-label={`Editing ${name}`}
          >
            {shape.type === "document" ? (
              <DocumentFormatting model={model} />
            ) : (
              <TableFormatting model={model} shape={shape} />
            )}
            <button
              title={
                shape.type === "table" ? "Table options" : "Document properties"
              }
              onClick={() => onProperties(shape.id)}
            >
              {shape.type === "table" ? "Table" : "Properties"}
            </button>
            <button
              className="primary"
              onClick={() => {
                editor.setEditingShape(null);
                editor.focus();
              }}
            >
              Done
            </button>
          </div>
        ) : (
          <div
            className="pdf-control-row"
            role="toolbar"
            aria-label={`${name} tools`}
          >
            {!viewableModel && <SaveToLibrary model={model} />}
            {shape.type === "image" && <MakeThreeDButton model={model} shape={shape} />}
            {viewableModel ? (
              <button
                className="model-open-button"
                aria-label={`Open 3D model ${name}`}
                onClick={() => onModel(viewableModel)}
              >
                <Box size={18} /> Open 3D
              </button>
            ) : (
              <button
                title={shape.type === "image" ? "Crop image" : "Edit content"}
                aria-label={
                  shape.type === "image" ? "Crop image" : "Edit content"
                }
                onClick={edit}
              >
                {shape.type === "image" ? (
                  <Crop size={17} />
                ) : (
                  <Pencil size={17} />
                )}
                {shape.type === "image" ? "Crop" : "Edit"}
              </button>
            )}
            {editableShape && nativeImage && shape.type === "image" && (
              <button
                type="button"
                title="Image opacity"
                aria-label={`Image opacity, ${Math.round(shape.opacity * 100)} percent`}
                aria-expanded={opacityOpen}
                onClick={() => setOpacityOpen(!opacityOpen)}
              >
                {Math.round(shape.opacity * 100)}%{" "}
                <span aria-hidden="true">⌄</span>
              </button>
            )}
            {editableShape && (
              <>
                <span className="pdf-control-divider" />
                <button
                  aria-label="Object properties"
                  title="Properties"
                  aria-expanded={propertiesOpen}
                  onClick={() => {
                    setOpacityOpen(false);
                    onProperties(shape.id);
                  }}
                >
                  <SlidersHorizontal size={17} /> Properties
                </button>
                <button
                  title="More actions"
                  aria-label={`More actions for ${name}`}
                  aria-expanded={panel}
                  onClick={() => {
                    setContextMenu(null);
                    setPanel(!panel);
                  }}
                >
                  <MoreVertical size={17} />
                </button>
              </>
            )}
          </div>
        )}
        {editableShape &&
          nativeImage &&
          shape.type === "image" &&
          opacityOpen &&
          !panel && (
            <div className="content-image-actions" aria-label="Image opacity">
              <OpacityControl
                model={model}
                shape={shape}
                label="Image opacity"
              />
            </div>
          )}
        {shape.type === "image" &&
          shape.props.assetId &&
          editor.getAsset(shape.props.assetId)?.meta.cadThumbnail && (
            <small className="file-preview-note">
              Saved DWG thumbnail · not a scaled drawing
            </small>
          )}
        {viewableModel && (
          <small className="file-preview-note">
            Interactive model opens separately to keep the canvas responsive.
          </small>
        )}
        {editableShape && panel && (
          <div
            className="pdf-options"
            data-context={!!contextPoint}
            style={
              contextPoint
                ? {
                    position: "fixed",
                    ...contextPoint,
                    right: "auto",
                    bottom: "auto",
                  }
                : undefined
            }
          >
            <div className="pdf-action-list" aria-label={`${name} actions`}>
              {viewableModel && (
                <>
                  <button
                    onClick={() => {
                      setPanel(false);
                      onModel(viewableModel);
                    }}
                  >
                    <Box size={16} /> Open 3D model
                  </button>
                  <span className="pdf-menu-divider" />
                </>
              )}
              {creative && (
                <>
                  <button onClick={() => openCreative("image")}>
                    <Sparkles size={16} />
                    {IMAGE_GENERATION_ENABLED
                      ? "Edit with AI"
                      : "View image edits · paused"}
                  </button>
                  {VIDEO_GENERATION_ENABLED && (
                    <button onClick={() => openCreative("video")}>
                      <Clapperboard size={16} />
                      Animate
                    </button>
                  )}
                  <span className="pdf-menu-divider" />
                </>
              )}
              {!!sketchSources.length && (
                <>
                  <button
                    onClick={() => {
                      setPanel(false);
                      const boxes = [shape, ...sketchSources]
                        .map((item) => editor.getShapePageBounds(item))
                        .filter((box): box is TLBox => !!box);
                      if (boxes.length)
                        editor.zoomToBounds(TLBox.Common(boxes), {
                          inset: 160,
                          animation: { duration: 200 },
                        });
                    }}
                  >
                    Compare with sketch
                  </button>
                  <button
                    onClick={() => {
                      setPanel(false);
                      editor.select(
                        ...sketchSources.map((source) => source.id),
                      );
                      editor.zoomToSelection({ animation: { duration: 200 } });
                    }}
                  >
                    Select original sketch
                  </button>
                  <span className="pdf-menu-divider" />
                </>
              )}
              {!viewableModel && (
                <button onClick={edit}>
                  {shape.type === "image" ? (
                    <Crop size={16} />
                  ) : (
                    <Pencil size={16} />
                  )}
                  {shape.type === "image" ? "Crop image" : "Edit content"}
                </button>
              )}
              <button
                onClick={() => {
                  setPanel(false);
                  editor.duplicateShapes([shape.id], { x: 24, y: 24 });
                }}
              >
                <Copy size={16} />
                Duplicate
              </button>
              <button
                onClick={() => {
                  setPanel(false);
                  editor.deleteShapes([shape.id]);
                }}
              >
                <Trash2 size={16} />
                Delete
              </button>
              <span className="pdf-menu-divider" />
              {downloadable && (
                <a
                  href={source}
                  download={name}
                  onClick={() => setPanel(false)}
                >
                  <Download size={16} />
                  Download original
                </a>
              )}
              <button
                onClick={() => {
                  setPanel(false);
                  editor.bringToFront([shape.id]);
                }}
              >
                <ArrowUpToLine size={16} />
                Bring to front
              </button>
              <button
                onClick={() => {
                  setPanel(false);
                  editor.sendToBack([shape.id]);
                }}
              >
                <ArrowDownToLine size={16} />
                Send to back
              </button>
              <button
                onClick={() => {
                  setPanel(false);
                  editor.toggleLock([shape.id]);
                  editor.selectNone();
                }}
              >
                <Lock size={16} />
                Lock
              </button>
              <button
                onClick={() => {
                  setPanel(false);
                  onProperties(shape.id);
                }}
              >
                <SlidersHorizontal size={16} />
                Properties
              </button>
            </div>
          </div>
        )}
        {editableShape && panel && (
          <button
            className="sr-only"
            aria-label="Close object actions"
            onClick={() => setPanel(false)}
          >
            <X size={14} />
          </button>
        )}
      </section>
    </div>
  );
}
